import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../../context/AuthContext';
import api from '../../../../../services/api';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { parseLocalDateTime } from '../../../utils/dateUtils';
import '../TaskModals.css';
import './CreateTodoModal.css';

const emptyTodo = () => ({
  id: Date.now() + Math.random(),
  description: '',
  departmentId: '',
  employeeId: '',
  supervisorId: '',
  priority: 'medium',
  dueDate: '',
  status: 'idle', // 'idle' | 'creating' | 'success' | 'failed'
  createdTaskId: null,
  error: null
});

const CreateTodoModal = ({ 
  isOpen,
  onClose,
  onSubmit,
  onUpdate,
  departments = [],
  employees = [],
  isSubmitting = false
}) => {
  const { company } = useAuth();
  
  const [todos, setTodos] = useState([emptyTodo()]);
  const [supervisors, setSupervisors] = useState([]);
  const [isLoadingSupervisors, setIsLoadingSupervisors] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [creatingIndex, setCreatingIndex] = useState(-1);

  const toLocalDateTimeInput = (value) => {
    try {
      if (!value) return '';
      if (typeof value === 'string' && value.includes('T') && value.length >= 16 && !value.endsWith('Z')) {
        return value.slice(0, 16);
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const tzOffsetMinutes = date.getTimezoneOffset();
      const localMillis = date.getTime() - tzOffsetMinutes * 60000;
      return new Date(localMillis).toISOString().slice(0, 16);
    } catch (_) {
      return '';
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    // Match CreateTaskModal behavior, but don't block on company.id (it can be null briefly on load)
    fetchSupervisors();
  }, [isOpen]);

  const fetchSupervisors = async () => {
    setIsLoadingSupervisors(true);
    try {
      const response = await api.get('/api/companies/users');
      if (response.data?.is_success) {
        const supervisorList = response.data.users.filter(user => 
          (user.role === 'admin' || user.role === 'supervisor') && user.isActive
        );
        setSupervisors(supervisorList);
      }
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    } finally {
      setIsLoadingSupervisors(false);
    }
  };

  const getFilteredEmployees = (departmentId) => {
    if (!departmentId) return [];
    return employees.filter(emp =>
      emp.departments && emp.departments.some(dept => dept.id == departmentId)
    );
  };

  const getFilteredSupervisors = (departmentId) => {
    if (!departmentId) return supervisors;
    return supervisors.filter(sup => {
      if (sup.role === 'admin') return true;
      return sup.departments && sup.departments.some(dept => 
        Number(dept.id) === Number(departmentId)
      );
    });
  };

  const resetForm = () => {
    setTodos([emptyTodo()]);
    setValidationErrors({});
    setCreatingIndex(-1);
  };

  const updateTodo = (index, field, value) => {
    setTodos(prev => {
      const updated = [...prev];
      const before = updated[index];
      updated[index] = { ...before, [field]: value };
      // Reset employee/supervisor if department changes
      if (field === 'departmentId') {
        updated[index].employeeId = '';
        updated[index].supervisorId = '';
      }
      // If user edits a failed row, clear its failed state so it can be re-submitted cleanly
      if (updated[index].status === 'failed') {
        updated[index].status = 'idle';
        updated[index].error = null;
      }
      // If user edits a successful row, don't allow re-create; mark it as needing update instead
      if (before?.status === 'success' && before?.createdTaskId) {
        updated[index].status = 'dirty';
        updated[index].error = null;
      }
      return updated;
    });
    // Clear validation error for this field
    if (validationErrors[`${index}-${field}`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${index}-${field}`];
        return newErrors;
      });
    }
  };

  const addRow = () => {
    setTodos(prev => [...prev, emptyTodo()]);
  };

  const removeRow = (index) => {
    if (todos.length === 1) return;
    setTodos(prev => prev.filter((_, i) => i !== index));
  };

  const isRowEmpty = (todo) => {
    const hasDesc = !!String(todo?.description || '').trim();
    const hasDept = !!String(todo?.departmentId || '');
    const hasDue = !!String(todo?.dueDate || '');
    return !hasDesc && !hasDept && !hasDue;
  };

  const validateTodos = (indicesToValidate) => {
    const errors = {};
    const currentDate = new Date();

    indicesToValidate.forEach((index) => {
      const todo = todos[index];
      if (!todo) return;
      // Don't block submission on blank rows
      if (isRowEmpty(todo)) return;

      if (!todo.description.trim()) {
        errors[`${index}-description`] = 'Required';
      }
      if (!todo.departmentId) {
        errors[`${index}-departmentId`] = 'Required';
      }
      if (!todo.dueDate) {
        errors[`${index}-dueDate`] = 'Required';
      } else {
        const dueDate = parseLocalDateTime(todo.dueDate);
        if (!dueDate) {
          errors[`${index}-dueDate`] = 'Invalid';
        } else if (dueDate <= currentDate) {
          errors[`${index}-dueDate`] = 'Must be future';
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createOne = async (index) => {
    const todo = todos[index];
    if (!todo) return;
    // Prevent duplicates: successful rows can't be "created" again. If edited, they become 'dirty' and should be updated.
    if (todo.status === 'success' || todo.status === 'dirty') return;
    if (isRowEmpty(todo)) return;

    setCreatingIndex(index);
    setTodos((prev) => {
      const updated = [...prev];
      const row = updated[index];
      if (!row) return prev;
      updated[index] = { ...row, status: 'creating', error: null };
      return updated;
    });

    const submitData = {
      description: todo.description,
      departmentId: todo.departmentId,
      employeeId: todo.employeeId,
      supervisorId: todo.supervisorId,
      priority: todo.priority,
      dueDate: todo.dueDate,
      taskType: 'task',
      location: { coordinates: { lat: null, lng: null }, address: '' },
      images: [],
      customer: null
    };

    const result = await onSubmit(submitData);

    setTodos((prev) => {
      const updated = [...prev];
      const row = updated[index];
      if (!row) return prev;

      if (result?.success) {
        updated[index] = {
          ...row,
          status: 'success',
          createdTaskId: result?.task?.id ?? null,
          error: null
        };
      } else {
        updated[index] = {
          ...row,
          status: 'failed',
          error: result?.error || 'Failed to create task'
        };
      }
      return updated;
    });

    setCreatingIndex(-1);
  };

  const handleSubmit = async () => {
    const indicesToCreate = todos
      .map((t, idx) => ({ t, idx }))
      .filter(({ t }) => t.status !== 'success' && t.status !== 'dirty' && !isRowEmpty(t))
      .map(({ idx }) => idx);

    const indicesToUpdate = todos
      .map((t, idx) => ({ t, idx }))
      .filter(({ t }) => t.status === 'dirty' && !!t.createdTaskId && !isRowEmpty(t))
      .map(({ idx }) => idx);

    if (indicesToCreate.length === 0 && indicesToUpdate.length === 0) return;
    if (!validateTodos([...indicesToCreate, ...indicesToUpdate])) return;

    for (const idx of indicesToCreate) {
      // eslint-disable-next-line no-await-in-loop
      await createOne(idx);
    }

    for (const idx of indicesToUpdate) {
      // eslint-disable-next-line no-await-in-loop
      await updateOne(idx);
    }
  };

  const retryFailed = async () => {
    const failedIndices = todos
      .map((t, idx) => ({ t, idx }))
      .filter(({ t }) => t.status === 'failed')
      .map(({ idx }) => idx);

    if (failedIndices.length === 0) return;
    if (!validateTodos(failedIndices)) return;

    for (const idx of failedIndices) {
      // eslint-disable-next-line no-await-in-loop
      await createOne(idx);
    }
  };

  const retryRow = async (index) => {
    if (!validateTodos([index])) return;
    await createOne(index);
  };

  const updateOne = async (index) => {
    const todo = todos[index];
    if (!todo?.createdTaskId) return;
    if (typeof onUpdate !== 'function') {
      setTodos((prev) => {
        const updated = [...prev];
        const row = updated[index];
        if (!row) return prev;
        updated[index] = { ...row, status: 'failed', error: 'Update handler not provided' };
        return updated;
      });
      return;
    }

    setCreatingIndex(index);
    setTodos((prev) => {
      const updated = [...prev];
      const row = updated[index];
      if (!row) return prev;
      updated[index] = { ...row, status: 'creating', error: null };
      return updated;
    });

    const updatePayload = {
      description: todo.description,
      departmentId: todo.departmentId,
      priority: todo.priority,
      dueDate: todo.dueDate ? new Date(todo.dueDate).toISOString() : null,
      // Match existing edit flow mapping
      supervisorAssigned: todo.supervisorId ? parseInt(todo.supervisorId) : null,
      employeeAssigned: todo.employeeId ? parseInt(todo.employeeId) : null
    };

    const result = await onUpdate(todo.createdTaskId, updatePayload);

    setTodos((prev) => {
      const updated = [...prev];
      const row = updated[index];
      if (!row) return prev;
      if (result?.success) {
        updated[index] = {
          ...row,
          status: 'success',
          createdTaskId: result?.task?.id ?? row.createdTaskId,
          error: null
        };
      } else {
        updated[index] = {
          ...row,
          status: 'failed',
          error: result?.error || 'Failed to update task'
        };
      }
      return updated;
    });

    setCreatingIndex(-1);
  };

  const updateRow = async (index) => {
    if (!validateTodos([index])) return;
    await updateOne(index);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  // Close on ESC
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    if (isOpen) {
      window.addEventListener('keydown', onKeyDown);
    }
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const isCreating = creatingIndex >= 0;
  const failedCount = todos.filter((t) => t.status === 'failed').length;
  const successCount = todos.filter((t) => t.status === 'success').length;
  const updateableCount = todos.filter((t) => t.status === 'dirty' && !!t.createdTaskId && !isRowEmpty(t)).length;
  const createableCount = todos.filter((t) => t.status !== 'success' && t.status !== 'dirty' && !isRowEmpty(t)).length;
  const actionableCount = createableCount + updateableCount;

  return (
    <div className="modal-overlay task-modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content task-modal-container todo-table-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header task-modal-header">
          <div className="task-modal-header-content">
            <div className="task-title-row">
              <span className="task-id-pill">Create Todos</span>
              <span style={{ 
                background: '#dbeafe', 
                color: '#1d4ed8', 
                padding: '2px 8px', 
                borderRadius: 12, 
                fontSize: 11, 
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.04em'
              }}>
                {successCount} / {todos.length} Created
              </span>
            </div>
          </div>
          <button className="close-button" onClick={handleClose} aria-label="Close" title="Close">Close</button>
        </div>

        <div className="modal-body task-modal-body" style={{ padding: 0, overflow: 'auto' }}>
          <div className="todo-table-wrapper">
            <table className="todo-table">
              <thead>
                <tr>
                  <th style={{ width: '28%' }}>Description *</th>
                  <th style={{ width: '15%' }}>Due Date *</th>
                  <th style={{ width: '15%' }}>Team *</th>
                  <th style={{ width: '14%' }}>Supervisor</th>
                  <th style={{ width: '14%' }}>Field Staff</th>
                  <th style={{ width: '10%' }}>Priority</th>
                  <th style={{ width: '4%' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {todos.map((todo, index) => {
                  const filteredEmployees = getFilteredEmployees(todo.departmentId);
                  const filteredSupervisors = getFilteredSupervisors(todo.departmentId);
                  const isCurrentlyCreating = creatingIndex === index;

                  return (
                    <tr key={todo.id} className={isCurrentlyCreating ? 'creating' : ''}>
                      <td>
                        <input
                          type="text"
                          className={`todo-input ${validationErrors[`${index}-description`] ? 'error' : ''}`}
                          value={todo.description}
                          onChange={(e) => updateTodo(index, 'description', e.target.value)}
                          placeholder="What needs to be done?"
                          disabled={isCreating}
                        />
                        {todo.status === 'failed' && !!todo.error && (
                          <div className="todo-inline-error">{todo.error}</div>
                        )}
                      </td>
                      <td>
                        <input
                          type="datetime-local"
                          className={`todo-input ${validationErrors[`${index}-dueDate`] ? 'error' : ''}`}
                          value={todo.dueDate}
                          onChange={(e) => updateTodo(index, 'dueDate', e.target.value)}
                          min={toLocalDateTimeInput(new Date())}
                          disabled={isCreating}
                        />
                      </td>
                      <td>
                        <select
                          className={`todo-select ${validationErrors[`${index}-departmentId`] ? 'error' : ''}`}
                          value={todo.departmentId}
                          onChange={(e) => updateTodo(index, 'departmentId', e.target.value)}
                          disabled={isCreating}
                        >
                          <option value="">Select...</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="todo-select"
                          value={todo.supervisorId}
                          onChange={(e) => updateTodo(index, 'supervisorId', e.target.value)}
                          disabled={isCreating || !todo.departmentId || isLoadingSupervisors}
                        >
                          <option value="">{isLoadingSupervisors ? 'Loading...' : 'Optional'}</option>
                          {!isLoadingSupervisors && todo.departmentId && filteredSupervisors.length === 0 && (
                            <option value="" disabled>No supervisors found</option>
                          )}
                          {filteredSupervisors.map(sup => (
                            <option key={sup.id} value={sup.id}>
                              {sup.firstName} {sup.lastName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="todo-select"
                          value={todo.employeeId}
                          onChange={(e) => updateTodo(index, 'employeeId', e.target.value)}
                          disabled={isCreating || !todo.departmentId}
                        >
                          <option value="">Optional</option>
                          {filteredEmployees.map(emp => (
                            <option key={emp.id} value={emp.id}>
                              {emp.firstName} {emp.lastName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className="todo-select priority-select"
                          value={todo.priority}
                          onChange={(e) => updateTodo(index, 'priority', e.target.value)}
                          disabled={isCreating}
                          data-priority={todo.priority}
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </td>
                      <td>
                        <div className="todo-status-cell">
                          {isCurrentlyCreating ? (
                            <div className="todo-creating-spinner" title="Creating..." />
                          ) : todo.status === 'dirty' ? (
                            <div className="todo-status dirty" title={todo.createdTaskId ? `Edited task #${todo.createdTaskId}` : 'Edited'}>
                              ✎{todo.createdTaskId ? <span className="todo-task-id">#{todo.createdTaskId}</span> : null}
                              <button
                                type="button"
                                className="todo-update-btn"
                                onClick={() => updateRow(index)}
                                disabled={isCreating}
                              >
                                Update
                              </button>
                            </div>
                          ) : todo.status === 'success' ? (
                            <div className="todo-status success" title={todo.createdTaskId ? `Created task #${todo.createdTaskId}` : 'Created'}>
                              ✓{todo.createdTaskId ? <span className="todo-task-id">#{todo.createdTaskId}</span> : null}
                            </div>
                          ) : todo.status === 'failed' ? (
                            <div className="todo-status failed" title="Failed">
                              ×
                              <button
                                type="button"
                                className="todo-retry-btn"
                                onClick={() => retryRow(index)}
                                disabled={isCreating}
                              >
                                Retry
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="todo-remove-btn"
                              onClick={() => removeRow(index)}
                              disabled={todos.length === 1 || isCreating}
                              title="Remove row"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="todo-table-actions">
            <button
              type="button"
              className="todo-add-row-btn"
              onClick={addRow}
              disabled={isCreating}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add Another Task
            </button>
            {failedCount > 0 && (
              <button
                type="button"
                className="todo-retry-failed-btn"
                onClick={retryFailed}
                disabled={isCreating}
                title="Retry all failed rows"
              >
                Retry Failed ({failedCount})
              </button>
            )}
          </div>
        </div>

        <div className="modal-footer task-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={isSubmitting || isCreating || actionableCount === 0}
          >
            {isCreating
              ? `Creating ${creatingIndex + 1} of ${todos.length}...`
              : actionableCount > 0
                ? updateableCount > 0 && createableCount > 0
                  ? `Create ${createableCount} / Update ${updateableCount}`
                  : updateableCount > 0
                    ? `Update ${updateableCount} Task${updateableCount !== 1 ? 's' : ''}`
                    : `Create ${createableCount} Task${createableCount !== 1 ? 's' : ''}`
                : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTodoModal;
